/* ==========================================================================
   DBG Nova — core theme JavaScript (vanilla, no dependencies)
   Loaded with `defer`. Every feature is a small Web Component or a delegated
   listener so pages only pay for what they use.
   ========================================================================== */
(() => {
  'use strict';
  const N = window.Nova || { routes: {}, money: {}, strings: {}, settings: {} };
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, wait = 200) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

  /* ---------- Money (Indian grouping; toInclPaise only adds GST in 'exclusive_add' mode) ---------- */
  const toInclPaise = (paise) => {
    const m = N.money.gstMode === 'exclusive_add' ? (N.money.gstMultiplier || 1) : 1;
    return Math.round(paise * m);
  };
  const formatINR = (paise) => {
    const rupees = Math.round(paise / 100);
    const s = String(Math.abs(rupees));
    let out;
    if (s.length <= 3) out = s;
    else {
      const last3 = s.slice(-3);
      let rest = s.slice(0, -3);
      const parts = [];
      while (rest.length > 2) { parts.unshift(rest.slice(-2)); rest = rest.slice(0, -2); }
      if (rest) parts.unshift(rest);
      out = parts.join(',') + ',' + last3;
    }
    return (rupees < 0 ? '−' : '') + '₹' + out;
  };
  window.NovaMoney = { toInclPaise, formatINR };

  /* ---------- Toast ---------- */
  let toastTimer;
  const toast = (msg) => {
    const el = $('#nova-toast'); if (!el) return;
    el.textContent = msg; el.classList.add('is-visible');
    clearTimeout(toastTimer); toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  /* ---------- Reveal on scroll ---------- */
  const initReveal = (root = document) => {
    const els = $$('[data-reveal]:not(.is-revealed)', root);
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || N.settings.reveal === false) { els.forEach(e => e.classList.add('is-revealed')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(e => io.observe(e));
  };

  /* ---------- Image fade-in ---------- */
  document.addEventListener('load', (e) => { if (e.target.tagName === 'IMG') e.target.classList.add('is-loaded'); }, true);

  /* ---------- Drawers ---------- */
  class NovaDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open'); this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      const focusable = $('button, [href], input, select, textarea', this.panel);
      setTimeout(() => (focusable || this.panel).focus({ preventScroll: true }), 50);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open'); this.setAttribute('aria-hidden', 'true');
      if (!$$('.drawer.is-open').length) document.body.classList.remove('drawer-open');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
    toggle(opener) { this.classList.contains('is-open') ? this.close() : this.open(opener); }
  }
  customElements.define('nova-drawer', NovaDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { const d = document.getElementById(opener.dataset.drawerOpen); if (d && d.open) { e.preventDefault(); d.open(opener); } }
    const mOpen = e.target.closest('[data-modal-open]');
    if (mOpen) { const m = document.getElementById(mOpen.dataset.modalOpen); if (m) { m.hidden = false; m.classList.add('is-open'); document.body.classList.add('drawer-open'); } }
    const mClose = e.target.closest('[data-modal-close]');
    if (mClose) { const m = mClose.closest('.modal'); if (m) { m.classList.remove('is-open'); m.hidden = true; document.body.classList.remove('drawer-open'); } }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') $$('.modal.is-open').forEach(m => { m.classList.remove('is-open'); m.hidden = true; document.body.classList.remove('drawer-open'); }); });

  /* ---------- Announcement rotator ---------- */
  class NovaAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = $$('.announcement__item', this); if (this.items.length < 2) return;
      this.i = 0; this.interval = parseInt(this.dataset.interval || '6000', 10);
      $('[data-ann-prev]', this)?.addEventListener('click', () => this.go(-1));
      $('[data-ann-next]', this)?.addEventListener('click', () => this.go(1));
      this.start();
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => this.start());
    }
    start() { clearInterval(this.timer); if (!reduceMotion) this.timer = setInterval(() => this.go(1), this.interval); }
    go(d) { this.items[this.i].classList.remove('is-active'); this.i = (this.i + d + this.items.length) % this.items.length; this.items[this.i].classList.add('is-active'); }
  }
  customElements.define('nova-announcement', NovaAnnouncement);

  /* ---------- Header: sticky state, mega menu, mobile search ---------- */
  class NovaHeader extends HTMLElement {
    connectedCallback() {
      this.items = $$('.nav__item--mega', this);
      let hoverTimer;
      this.items.forEach(item => {
        const link = $('.nav__link', item);
        item.addEventListener('mouseenter', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => this.openItem(item), 90); });
        item.addEventListener('mouseleave', () => { clearTimeout(hoverTimer); hoverTimer = setTimeout(() => this.closeItem(item), 120); });
        link.addEventListener('click', (e) => {
          if (window.matchMedia('(hover: none)').matches || e.detail === 0) {
            if (!item.classList.contains('is-open')) { e.preventDefault(); this.closeAll(); this.openItem(item); }
          }
        });
        link.addEventListener('keydown', (e) => { if (e.key === 'ArrowDown') { e.preventDefault(); this.openItem(item); $('a', $('.mega', item))?.focus(); } });
      });
      document.addEventListener('click', (e) => { if (!e.target.closest('.nav__item--mega')) this.closeAll(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
      // sticky shadow / shrink
      const onScroll = () => { this.classList.toggle('is-stuck', window.scrollY > 24); };
      onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
      // mobile search
      const search = $('nova-search', this);
      $$('[data-search-open]', this).forEach(b => b.addEventListener('click', () => { search?.openMobile(); }));
    }
    openItem(item) { this.closeAll(item); item.classList.add('is-open'); $('.nav__link', item).setAttribute('aria-expanded', 'true'); }
    closeItem(item) { item.classList.remove('is-open'); $('.nav__link', item).setAttribute('aria-expanded', 'false'); }
    closeAll(except) { this.items.forEach(i => { if (i !== except) this.closeItem(i); }); }
  }
  customElements.define('nova-header', NovaHeader);

  /* ---------- Predictive search ---------- */
  class NovaSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.panel = $('.search-panel', this);
      this.results = $('[data-results]', this);
      this.defaults = $('.search-panel__default', this);
      this.clearBtn = $('.search-form__clear', this);
      this.form = $('form', this);
      if (!this.input) return;
      this.input.addEventListener('focus', () => this.openPanel());
      this.input.addEventListener('input', debounce(() => this.onInput(), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.clearBtn?.addEventListener('click', () => { this.input.value = ''; this.onInput(); this.input.focus(); });
      this.form.addEventListener('submit', () => this.remember(this.input.value));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closePanel(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closePanel(); this.closeMobile(); } });
      this.renderRecent();
    }
    openMobile() { this.classList.add('is-open'); document.body.classList.add('drawer-open'); setTimeout(() => this.input.focus(), 60); this.openPanel(); if (!$('.search-form__mobile-close', this)) { const b = document.createElement('button'); b.type = 'button'; b.className = 'btn btn--ghost btn--sm search-form__mobile-close'; b.textContent = 'Close'; b.style.marginTop = '8px'; b.addEventListener('click', () => this.closeMobile()); this.form.after(b); } }
    closeMobile() { this.classList.remove('is-open'); document.body.classList.remove('drawer-open'); }
    openPanel() { this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true'); }
    closePanel() { this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
    async onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = !q;
      if (q.length < 2) { this.results.hidden = true; this.results.innerHTML = ''; this.defaults.hidden = false; return; }
      this.abort?.abort(); this.abort = new AbortController();
      const url = `${N.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const html = await fetch(url, { signal: this.abort.signal }).then(r => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = $('.ps', doc);
        this.results.innerHTML = inner ? inner.outerHTML : '';
        this.results.hidden = false; this.defaults.hidden = true; this.openPanel();
        this.focusIndex = -1;
      } catch (e) { if (e.name !== 'AbortError') console.warn('search', e); }
    }
    onKey(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        this.focusIndex = (this.focusIndex ?? -1) + (e.key === 'ArrowDown' ? 1 : -1);
        this.focusIndex = Math.max(0, Math.min(options.length - 1, this.focusIndex));
        options.forEach((o, i) => o.classList.toggle('is-focused', i === this.focusIndex));
        options[this.focusIndex].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && this.focusIndex >= 0) { e.preventDefault(); this.remember(this.input.value); options[this.focusIndex].click(); }
    }
    remember(q) { q = (q || '').trim(); if (!q) return; try { const list = JSON.parse(localStorage.getItem('nova:recent') || '[]').filter(x => x !== q); list.unshift(q); localStorage.setItem('nova:recent', JSON.stringify(list.slice(0, 6))); } catch (e) { /* storage unavailable */ } }
    renderRecent() {
      try {
        const list = JSON.parse(localStorage.getItem('nova:recent') || '[]'); const wrap = $('[data-recent-searches]', this); const ul = $('[data-recent-list]', this);
        if (!list.length || !wrap) return;
        ul.innerHTML = list.map(q => `<a href="${N.routes.search}?q=${encodeURIComponent(q)}&type=product" class="chip">${q.replace(/[<>&"]/g, '')}</a>`).join('');
        wrap.hidden = false;
      } catch (e) { /* ignore */ }
    }
  }
  customElements.define('nova-search', NovaSearch);

  /* ---------- Cart ---------- */
  const cartSections = () => ['cart-drawer'];
  const updateCartCount = (count) => { $$('[data-cart-count]').forEach(el => { el.textContent = count; el.classList.toggle('is-empty', !count); }); };

  // Shopify only parses a JSON body on /cart/change and /cart/update when the request looks like an
  // Ajax call (Accept + X-Requested-With). Without these headers it treats the POST as a form submit,
  // ignores the body and answers with the cart page — which is why "remove" used to do nothing.
  const cartHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

  class CartDrawer extends NovaDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('change', (e) => { const inp = e.target.closest('.qty__input[data-line-key]'); if (inp) this.change(inp.dataset.lineKey, parseInt(inp.value, 10) || 0, inp.closest('.cart-line')); });
      this.addEventListener('click', (e) => { const rm = e.target.closest('[data-remove-line]'); if (rm) this.change(rm.dataset.removeLine, 0, rm.closest('.cart-line')); });
      this.addEventListener('change', debounce((e) => { if (e.target.matches('[data-cart-note]')) fetch(N.routes.cartUpdate, { method: 'POST', headers: cartHeaders, body: JSON.stringify({ note: e.target.value }) }); }, 400));
    }
    renderFrom(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-drawer-content]', doc);
      if (fresh) $('[data-cart-drawer-content]', this).replaceWith(fresh);
      const count = $('[data-cart-drawer-count]', this)?.textContent.replace(/\D/g, '') || '0';
      updateCartCount(parseInt(count, 10) || 0);
    }
    async refresh() {
      const html = await fetch(`${N.routes.cart}?section_id=cart-drawer`).then(r => r.text());
      this.renderFrom(html);
    }
    async change(key, quantity, line) {
      if (this.busy) return; this.busy = true;
      this.classList.add('is-loading');
      if (line) line.classList.add(quantity === 0 ? 'is-removing' : 'is-updating');
      const body = $('.drawer__body', this); const scrollTop = body ? body.scrollTop : 0;
      try {
        const res = await fetch(N.routes.cartChange, { method: 'POST', headers: cartHeaders, body: JSON.stringify({ id: key, quantity, sections: cartSections(), sections_url: window.location.pathname }) });
        const data = await res.json();
        if (res.status >= 400) throw new Error(data.description || data.message || 'cart');
        if (data.sections && data.sections['cart-drawer']) this.renderFrom(data.sections['cart-drawer']); else await this.refresh();
        if (data.item_count !== undefined) updateCartCount(data.item_count);
        if (quantity === 0) toast(N.strings.removed || 'Removed from cart');
        const nb = $('.drawer__body', this); if (nb) nb.scrollTop = scrollTop;
      } catch (e) { if (line) line.classList.remove('is-removing', 'is-updating'); toast(N.strings.cartError || 'Could not update cart'); }
      this.classList.remove('is-loading'); this.busy = false;
    }
  }
  customElements.define('cart-drawer', CartDrawer);
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-cart-open]');
    if (a) { const d = $('cart-drawer'); if (d) { e.preventDefault(); d.open(a); } }
  });

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this); if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      const btn = $('[type="submit"]', this.form); const err = $('[data-form-error]', this);
      if (btn?.disabled) return;
      btn?.classList.add('btn--loading'); btn?.setAttribute('aria-disabled', 'true');
      if (err) { err.hidden = true; err.textContent = ''; }
      const fd = new FormData(this.form);
      fd.append('sections', cartSections().join(',')); fd.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(N.routes.cartAdd, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: fd });
        const data = await res.json();
        if (res.status >= 400) throw new Error(data.description || data.message || N.strings.cartError);
        const drawer = $('cart-drawer');
        if (drawer) { if (data.sections && data.sections['cart-drawer']) drawer.renderFrom(data.sections['cart-drawer']); else await drawer.refresh(); drawer.open(btn); }
        toast(N.strings.added || 'Added to cart');
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (ex) {
        if (err) { err.textContent = ex.message; err.hidden = false; } else toast(ex.message);
      }
      btn?.classList.remove('btn--loading'); btn?.removeAttribute('aria-disabled');
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      $('[data-qty-minus]', this)?.addEventListener('click', () => this.step(-1));
      $('[data-qty-plus]', this)?.addEventListener('click', () => this.step(1));
    }
    step(d) { const min = parseInt(this.input.min || '0', 10); const v = Math.max(min, (parseInt(this.input.value, 10) || 0) + d); this.input.value = v; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.items = $$('.product__stage-item', this); this.thumbs = $$('.product__thumb', this);
      if (!this.items.length) return;
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      let x0 = null; const stage = $('.product__stage', this);
      stage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => { if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1)); x0 = null; }, { passive: true });
    }
    go(i) {
      if (!this.items.length) return;
      i = (i + this.items.length) % this.items.length;
      this.items.forEach((it, k) => { it.classList.toggle('is-active', k === i); if (k !== i) $$('video', it).forEach(v => v.pause()); });
      this.thumbs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
      this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      this.index = i;
    }
    setMedia(id) { const i = this.items.findIndex(it => it.dataset.mediaId === String(id)); if (i >= 0) this.go(i); }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try { this.product = JSON.parse($('[data-product-json]', this).textContent); } catch (e) { return; }
      this.section = this.closest('[data-section-id]');
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selectedOptions() {
      return $$('fieldset', this).map(fs => { const checked = $('input:checked', fs); return checked ? checked.value : null; });
    }
    currentVariant() {
      const opts = this.selectedOptions();
      if (!opts.length) return this.product.variants[0];
      return this.product.variants.find(v => v.options.every((o, i) => o === opts[i]));
    }
    onChange() {
      const v = this.currentVariant();
      $$('fieldset', this).forEach(fs => { const sel = $('input:checked', fs); const lbl = $('[data-selected-value]', fs); if (sel && lbl) lbl.textContent = sel.value; });
      this.updateAvailability();
      const root = this.section || document;
      const idInput = $('[data-variant-id]', root); const atc = $('[data-add-to-cart]', root); const label = $('[data-atc-label]', root);
      const stock = $('[data-stock]', root); const sticky = $('[data-sticky-atc]', root); const stickyPrice = $('[data-sticky-price]', root); const sku = $('[data-variant-sku]', root);
      if (!v) { if (atc) { atc.disabled = true; label.textContent = N.strings.unavailable; } return; }
      if (idInput) idInput.value = v.id;
      if (atc) { atc.disabled = !v.available; label.textContent = v.available ? N.strings.addToCart : N.strings.soldOut; }
      if (sticky) { sticky.disabled = !v.available; sticky.textContent = v.available ? N.strings.addToCart : N.strings.soldOut; }
      if (stock) stock.innerHTML = v.available ? `<span class="stock stock--in">✓ In stock</span>` : `<span class="stock stock--out">Out of stock</span>`;
      if (sku) sku.textContent = v.sku || '';
      // price
      const price = toInclPaise(v.price); const cmp = v.compare_at_price ? toInclPaise(v.compare_at_price) : 0;
      const now = $('[data-price-now]', root); const mrp = $('[data-price-mrp]', root); const save = $('[data-price-save]', root); const emi = $('[data-price-emi]', root);
      if (now) now.textContent = formatINR(price);
      if (mrp) { if (cmp > price) { mrp.innerHTML = `${N.money.mrpLabel} <s>${formatINR(cmp)}</s>`; mrp.hidden = false; } else mrp.hidden = true; }
      if (save) { if (cmp > price) { save.textContent = `${N.strings.save} ${formatINR(cmp - price)} (${Math.round((cmp - price) * 100 / cmp)}%)`; save.hidden = false; } else save.hidden = true; }
      if (emi) { if (N.money.emiEnabled && price >= N.money.emiMin) { emi.innerHTML = `${N.strings.emiOr} <strong>${formatINR(Math.round(price / N.money.emiMonths))}/${N.strings.monthShort}</strong> ${N.money.emiLabel}`; emi.hidden = false; } else emi.hidden = true; }
      if (stickyPrice) stickyPrice.textContent = formatINR(price);
      const incl = $('[data-price-incl]', root); if (incl) incl.textContent = formatINR(Math.round(v.price * (N.money.gstMultiplier || 1)));
      // media
      if (v.featured_media) $('product-gallery', root)?.setMedia(v.featured_media.id);
      // url
      const url = new URL(window.location.href); url.searchParams.set('variant', v.id); window.history.replaceState({}, '', url);
    }
    updateAvailability() {
      const opts = this.selectedOptions();
      $$('fieldset', this).forEach((fs, i) => {
        $$('input', fs).forEach(inp => {
          const test = opts.slice(); test[i] = inp.value;
          const exists = this.product.variants.some(v => v.available && v.options.every((o, k) => test[k] === null || o === test[k]));
          inp.nextElementSibling?.classList.toggle('is-unavailable', !exists);
        });
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Sticky add-to-cart bar (mobile PDP) ---------- */
  const initSticky = () => {
    const bar = $('[data-sticky-bar]'); const atc = $('[data-add-to-cart]'); if (!bar || !atc) return;
    document.body.classList.add('has-sticky-bar');
    const io = new IntersectionObserver(([en]) => { bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0); bar.setAttribute('aria-hidden', en.isIntersecting ? 'true' : 'false'); }, { threshold: 0 });
    io.observe(atc);
    $('[data-sticky-atc]', bar)?.addEventListener('click', () => atc.closest('form')?.requestSubmit());
  };

  /* ---------- Pincode / delivery estimate ---------- */
  class PincodeCheck extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this); this.result = $('[data-pincode-result]', this); this.btn = $('[data-pincode-check]', this);
      this.btn?.addEventListener('click', () => this.check());
      this.input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.check(); } });
      try { const saved = localStorage.getItem('nova:pincode'); if (saved) { this.input.value = saved; this.check(); } } catch (e) { /* ignore */ }
    }
    // Smart locks: free installation everywhere, 24-hour service only in the listed pincodes.
    // Prefixes come from theme settings as "110=Delhi, 2013=Noida, …"; the longest prefix wins.
    lockLookup(pin) {
      const raw = this.dataset.lockPincodes || '';
      let best = null;
      raw.split(',').forEach((entry) => {
        const [prefix, city] = entry.split('=').map((x) => (x || '').trim());
        if (!prefix || !pin.startsWith(prefix)) return;
        if (!best || prefix.length > best.prefix.length) best = { prefix, city: city || '' };
      });
      return best;
    }
    renderLock(pin) {
      const card = $('[data-lock-card]', this);
      if (!card) return;
      const mark = $('[data-lock-mark]', card);
      const title = $('[data-lock-title]', card);
      const cityEl = $('[data-lock-city]', card);
      const note = $('[data-lock-note]', card);
      card.hidden = false;
      card.classList.remove('is-in', 'is-out');
      card.classList.add('is-checking');
      mark.textContent = '';
      title.textContent = 'Checking service cover…';
      cityEl.textContent = '';
      note.textContent = '';
      const delay = reduceMotion ? 0 : 650;
      clearTimeout(this._lockTimer);
      this._lockTimer = setTimeout(() => {
        const hit = this.lockLookup(pin);
        card.classList.remove('is-checking');
        if (hit) {
          card.classList.add('is-in');
          mark.textContent = '✓';
          title.textContent = this.dataset.lockIn || 'Free installation + 24-hour service';
          cityEl.textContent = hit.city ? `${hit.city} · ${pin}` : pin;
          note.textContent = '';
        } else {
          card.classList.add('is-out');
          mark.textContent = '🛠';
          title.textContent = this.dataset.lockOut || 'Free installation available';
          cityEl.textContent = pin;
          note.textContent = this.dataset.lockOutNote || '';
        }
      }, delay);
    }
    async check() {
      const pin = (this.input.value || '').trim();
      if (!/^[1-9][0-9]{5}$/.test(pin)) { this.result.textContent = 'Enter a valid 6-digit pincode.'; this.result.style.color = 'var(--color-danger)'; return; }
      try { localStorage.setItem('nova:pincode', pin); } catch (e) { /* ignore */ }
      this.result.style.color = '';
      if (this.hasAttribute('data-lock-service')) this.renderLock(pin);
      const endpoint = this.dataset.endpoint;
      if (endpoint) {
        this.result.textContent = 'Checking…';
        try {
          const data = await fetch(`${endpoint}${endpoint.includes('?') ? '&' : '?'}pincode=${pin}`).then(r => r.json());
          if (data && data.serviceable === false) { this.result.textContent = 'Sorry, we cannot deliver to this pincode yet. WhatsApp us for options.'; this.result.style.color = 'var(--color-danger)'; return; }
          const edd = data?.edd || data?.estimated_delivery || '';
          this.result.innerHTML = `✓ Delivery to <strong>${pin}</strong>${edd ? ` by <strong>${edd}</strong>` : ''}${data?.cod === false ? ' · Prepaid only' : ''}`;
          return;
        } catch (e) { /* fall through to generic */ }
      }
      const metro = /^(4000|4001|4007|1100|1101|1102|5600|5601|5000|6000|7000|4110|3800)/.test(pin);
      const d1 = new Date(); d1.setDate(d1.getDate() + (metro ? 2 : 4)); const d2 = new Date(); d2.setDate(d2.getDate() + (metro ? 3 : 6));
      const f = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      this.result.innerHTML = `✓ Delivery to <strong>${pin}</strong>: <strong>${f(d1)} – ${f(d2)}</strong> · Free`;
    }
  }
  customElements.define('pincode-check', PincodeCheck);

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url; if (!url) return;
      const load = async () => {
        try { const html = await fetch(url).then(r => r.text()); const doc = new DOMParser().parseFromString(html, 'text/html'); const inner = $('product-recommendations', doc); if (inner && inner.innerHTML.trim()) { this.innerHTML = inner.innerHTML; initReveal(this); } } catch (e) { /* ignore */ }
      };
      if ('IntersectionObserver' in window) { const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '300px' }); io.observe(this); } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Facet filters (collection & search) ---------- */
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this); this.sectionId = this.dataset.section;
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); $('nova-drawer', this)?.close(); });
      this.form.addEventListener('change', (e) => { if (e.target.matches('[data-sort]') || e.target.type === 'checkbox') this.apply(); });
      this.addEventListener('click', (e) => { const a = e.target.closest('[data-facet-remove], [data-facet-clear]'); if (a) { e.preventDefault(); this.load(a.href); } });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    buildUrl() {
      const fd = new FormData(this.form); const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) { if (v !== '' && v !== null) params.append(k, v); }
      // convert GST-inclusive price inputs back to stored (exclusive) prices
      const pf = $('[data-price-facet]', this.form);
      if (pf) { const m = parseFloat(pf.dataset.multiplier || '1'); $$('[data-price-input]', pf).forEach(inp => { if (inp.value) params.set(inp.name, String(Math.round(parseFloat(inp.value) / m))); }); }
      return `${this.form.action.split('?')[0]}?${params.toString()}`;
    }
    apply() { this.load(this.buildUrl()); }
    async load(url, push = true) {
      const container = $('#ProductGridContainer'); const section = this.closest('[data-section-id]');
      section?.classList.add('is-loading');
      try {
        const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${this.sectionId}`;
        const html = await fetch(fetchUrl).then(r => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('#ProductGridContainer', doc);
        if (fresh && container) { container.replaceWith(fresh); initReveal(fresh); }
        if (push) window.history.pushState({}, '', url);
        const top = $('.facets__bar')?.getBoundingClientRect().top; if (top !== undefined && top < 0) window.scrollTo({ top: window.scrollY + top - 80, behavior: reduceMotion ? 'auto' : 'smooth' });
      } catch (e) { window.location.href = url; }
      section?.classList.remove('is-loading');
    }
  }
  customElements.define('facet-filters-form', FacetFiltersForm);

  /* ---------- YouTube facade ---------- */
  class YoutubeFacade extends HTMLElement {
    connectedCallback() {
      $('button', this)?.addEventListener('click', () => {
        const id = this.dataset.id; const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;
        iframe.title = this.dataset.title || 'Video'; iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture'; iframe.allowFullscreen = true;
        this.innerHTML = ''; this.appendChild(iframe);
      });
    }
  }
  customElements.define('youtube-facade', YoutubeFacade);

  /* ---------- Hero: lazy video + slides + headline rise ---------- */
  class NovaHero extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.hero__slide', this); this.dots = $$('.hero__dot', this);
      this.interval = parseInt(this.dataset.interval || '7000', 10); this.autoplay = this.dataset.autoplay !== 'false';
      this.i = 0;
      // split headline words for rise animation
      $$('.hero__headline', this).forEach(h => { if (h.dataset.split) return; h.dataset.split = '1'; const words = h.textContent.trim().split(/\s+/); h.innerHTML = words.map((w, k) => `<span class="w" style="--i:${k}">${w}</span>`).join(' '); });
      this.dots.forEach((d, k) => d.addEventListener('click', () => { this.go(k); this.start(); }));
      const io = new IntersectionObserver((en) => { if (en[0].isIntersecting) { this.playVideo(this.slides[this.i]); this.start(); } else { this.pauseAll(); clearInterval(this.timer); } }, { threshold: 0.2 });
      io.observe(this);
    }
    // No rendition guessing. Two attempts at being clever here both backfired:
    // probing with fetch(HEAD) downloaded the whole file because Shopify's video CDN
    // ignores the method, and walking a candidate chain on the <video> error event
    // downloaded 61MB on desktop because load() on a live element fires error itself.
    // Use exactly the source Liquid gave us, once. Size is the merchant's job, not a
    // thing JavaScript can negotiate after the bytes are already moving.
    playYouTube(slide) {
      const box = $('.hero__yt', slide); if (!box) return false;
      if (!box.dataset.loaded) {
        box.dataset.loaded = '1';
        const id = box.dataset.yt;
        const f = document.createElement('iframe');
        f.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3`;
        f.allow = 'autoplay; encrypted-media'; f.title = ''; f.tabIndex = -1; f.setAttribute('aria-hidden', 'true');
        const fit = () => { const w = box.clientWidth, h = box.clientHeight; const iw = Math.max(w, h * 16 / 9), ih = Math.max(h, w * 9 / 16); f.width = Math.ceil(iw); f.height = Math.ceil(ih); f.style.width = Math.ceil(iw) + 'px'; f.style.height = Math.ceil(ih) + 'px'; };
        fit(); window.addEventListener('resize', fit, { passive: true });
        f.addEventListener('load', () => setTimeout(() => box.classList.add('is-playing'), 1200));
        box.appendChild(f);
      }
      return true;
    }
    playVideo(slide) {
      if (!this.autoplay || reduceMotion) return;
      const c = navigator.connection; if (c && (c.saveData || /(^|[^4-9])[23]g$/.test(c.effectiveType || ''))) return;
      if (this.playYouTube(slide)) return;
      const v = $('.hero__video', slide); if (!v) return;
      const mobile = window.innerWidth < 750;
      // Hero videos here run to tens of megabytes, so autoplay is opt-in per device
      // rather than on by default. Everyone else keeps the poster image, which is the
      // element that paints first anyway.
      const allowed = mobile ? this.dataset.videoMobile === 'true' : this.dataset.videoDesktop === 'true';
      if (!allowed) return;
      if (!v.src && !v.dataset.loading) {
        v.dataset.loading = '1';
        v.src = (mobile && v.dataset.srcMobile) ? v.dataset.srcMobile : v.dataset.srcDesktop;
        v.load();
      }
      v.play().then(() => v.classList.add('is-playing')).catch(() => {});
    }
    pauseAll() { $$('.hero__video', this).forEach(v => v.pause()); }
    go(k) {
      if (this.slides.length < 2) return;
      this.slides[this.i].classList.remove('is-active'); this.dots[this.i]?.classList.remove('is-active'); this.dots[this.i]?.setAttribute('aria-selected', 'false');
      this.i = (k + this.slides.length) % this.slides.length;
      this.slides[this.i].classList.add('is-active'); this.dots[this.i]?.classList.add('is-active'); this.dots[this.i]?.setAttribute('aria-selected', 'true');
      this.pauseAll(); this.playVideo(this.slides[this.i]);
      $$('.hero__headline', this.slides[this.i]).forEach(h => { h.style.animation = 'none'; h.querySelectorAll('.w').forEach(w => { w.style.animation = 'none'; void w.offsetWidth; w.style.animation = ''; }); });
    }
    start() { clearInterval(this.timer); if (this.slides.length > 1 && !reduceMotion) this.timer = setInterval(() => this.go(this.i + 1), this.interval); }
  }
  customElements.define('nova-hero', NovaHero);

  /* ---------- Count-up stats ---------- */
  const initCountUp = () => {
    const els = $$('[data-countup]'); if (!els.length || reduceMotion) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (!en.isIntersecting) return; io.unobserve(en.target);
        const raw = en.target.dataset.countup; const m = raw.match(/^(\d+)(.*)$/); if (!m) return;
        const target = parseInt(m[1], 10); const suffix = m[2]; const t0 = performance.now();
        const step = (t) => { const p = Math.min(1, (t - t0) / 1200); const eased = 1 - Math.pow(1 - p, 3); en.target.textContent = Math.round(target * eased) + suffix; if (p < 1) requestAnimationFrame(step); };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    els.forEach(e => io.observe(e));
  };

  /* ---------- Lazy inline videos (muted loops) ---------- */
  const initLazyVideos = () => {
    const els = $$('video[data-lazy-video]'); if (!els.length) return;
    const c = navigator.connection; const lite = reduceMotion || (c && (c.saveData || /(^|[^4-9])[23]g$/.test(c.effectiveType || '')));
    if (lite) return; // poster only
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        const v = en.target;
        if (en.isIntersecting) { if (!v.src) { v.src = v.dataset.src; v.load(); } v.play().then(() => v.classList.add('is-playing')).catch(() => {}); }
        else if (v.src) v.pause();
      });
    }, { rootMargin: '200px', threshold: 0.1 });
    els.forEach(v => io.observe(v));
  };

  /* ---------- Office hours badge (IST) ---------- */
  const initHours = () => {
    const els = $$('[data-hours]'); if (!els.length) return;
    const pad = (n) => String(n).padStart(2, '0');
    const fmt12 = (h) => `${((h + 11) % 12) + 1} ${h >= 12 ? 'pm' : 'am'}`;
    const tick = () => {
      const now = new Date();
      els.forEach((el) => {
        const tz = el.dataset.tz || 'Asia/Kolkata';
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour12: false, weekday: 'short', hour: 'numeric', minute: 'numeric' }).formatToParts(now);
        const get = (t) => (parts.find(p => p.type === t) || {}).value;
        const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        const day = dayMap[get('weekday')] ?? now.getDay();
        const h = parseInt(get('hour'), 10) % 24, m = parseInt(get('minute'), 10);
        const openH = parseInt(el.dataset.open, 10), closeH = parseInt(el.dataset.close, 10);
        const days = (el.dataset.days || '1,2,3,4,5,6').split(',').map(Number);
        const mins = h * 60 + m;
        const isDay = days.includes(day);
        const open = isDay && mins >= openH * 60 && mins < closeH * 60;
        const state = $('[data-hours-state]', el), detail = $('[data-hours-detail]', el);
        el.classList.toggle('is-open', open); el.classList.toggle('is-closed', !open);
        if (open) {
          const left = closeH * 60 - mins;
          state.textContent = 'Support is open';
          detail.textContent = `closes in ${Math.floor(left / 60)}h ${pad(left % 60)}m · till ${fmt12(closeH)} IST`;
        } else {
          // next opening
          let d = day, add = 0;
          if (isDay && mins < openH * 60) add = 0; else { do { d = (d + 1) % 7; add++; } while (!days.includes(d)); }
          const target = add * 1440 + openH * 60; const nowMin = (add === 0 ? mins : mins); const left = target - nowMin;
          const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const when = add === 0 ? 'today' : (add === 1 ? 'tomorrow' : names[d]);
          state.textContent = 'Support team is closed';
          detail.textContent = `opens ${when} at ${fmt12(openH)} IST · in ${Math.floor(left / 60)}h ${pad(left % 60)}m`;
        }
      });
    };
    tick(); setInterval(tick, 30000);
  };

  /* ---------- Boot ---------- */
  const boot = () => { initReveal(); initSticky(); initCountUp(); initLazyVideos(); initHours(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); initCountUp(); });
})();
